import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';

import * as schemas from 'store/common/schemas';
import {
  selectAllEntities,
  selectCompanyTeamMemberConnectionsEntities,
} from 'store/entities/selectors';
import * as helpers from 'store/features/company-team-member-connections/helpers';

const selectUsersTeamMembershipsFeatureState = state => state.features.users.teamMemberships;

const selectUsersTeamMembershipsFeatureStateById = createSelector(
  selectUsersTeamMembershipsFeatureState,
  featureState => featureState.byId
);

const selectUsersTeamMembershipsFeatureStateByUserId = createSelector(
  selectUsersTeamMembershipsFeatureState,
  featureState => featureState.byUserId
);

export const selectUserTeamMemberships = createSelector(
  selectCompanyTeamMemberConnectionsEntities,
  (state, props) => props.userId,
  selectAllEntities,
  (teamConnectionsById, userId, entities) =>
    denormalize(Object.keys(teamConnectionsById), [schemas.companyTeamMemberConnection], entities)
      .filter(ac => ac && ac.user && ac.user.id === userId)
      .map(ac => ({
        ...ac,
        status: helpers.calculateTeamMemberConnectionStatus({ ...ac }),
      }))
);

export const selectTeamMembership = createSelector(
  (state, props) => props.teamMembershipId,
  selectAllEntities,
  (teamMembershipId, entities) => {
    const team = denormalize(teamMembershipId, schemas.companyTeamMemberConnection, entities);
    if (team) team.status = helpers.calculateTeamMemberConnectionStatus(team);
    return team;
  }
);

export const selectIsFetchingUserTeamMemberships = createSelector(
  selectUsersTeamMembershipsFeatureStateByUserId,
  (state, props) => props.userId,
  (teamsFeatureByUserId, userId) =>
    teamsFeatureByUserId[userId] && teamsFeatureByUserId[userId].fetching
);

export const selectIsFetchedUserTeamMembershipsAtleastOnce = createSelector(
  selectUsersTeamMembershipsFeatureStateByUserId,
  (state, props) => props.userId,
  (teamsFeatureByUserId, userId) =>
    teamsFeatureByUserId[userId] && teamsFeatureByUserId[userId].lastFetchedAt
);

export const selectIsFetchingTeamMembership = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] && teamsFeatureById[teamMembershipId].fetching
);

export const selectIsFetchedTeamMembershipAtleastOnce = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] && teamsFeatureById[teamMembershipId].lastFetchedAt
);

export const selectIsCheckingAcceptTeamMembershipStatus = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] &&
    teamsFeatureById[teamMembershipId].checkingAcceptTeamMembershipStatus
);

export const selectIsCheckedAcceptTeamMembershipStatusAtleastOnce = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] &&
    !!teamsFeatureById[teamMembershipId].lastCheckedAcceptTeamMembershipStatusAt
);

export const selectIsAcceptingTeamMembership = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] && teamsFeatureById[teamMembershipId].accepting
);

export const selectIsRejectingTeamMembership = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] && teamsFeatureById[teamMembershipId].rejecting
);

export const selectIsRevokingTeamMembership = createSelector(
  selectUsersTeamMembershipsFeatureStateById,
  (state, props) => props.teamMembershipId,
  (teamsFeatureById, teamMembershipId) =>
    teamsFeatureById[teamMembershipId] && teamsFeatureById[teamMembershipId].revoking
);

export const selectIsPollingAnyTeamMembership = createSelector(
  selectCompanyTeamMemberConnectionsEntities,
  companyTeamMemberEntities => {
    const ids = Object.keys(companyTeamMemberEntities);
    for (let i = 0; i < ids.length; i++) {
      if (companyTeamMemberEntities[ids[i]] && companyTeamMemberEntities[ids[i]].polling)
        return true;
    }
    return false;
  }
);



// WEBPACK FOOTER //
// ./src/store/features/users/team-memberships/selectors.js